import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { PaymentMethodEntry } from '../types'

interface PaymentMethodOrderModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: Record<string, PaymentMethodEntry>
  onChange: (config: Record<string, PaymentMethodEntry>) => void
}

interface OrderItem {
  key: string
  label: string
  logoUrl?: string
  type: string
}

export function PaymentMethodOrderModal({
  open,
  onOpenChange,
  value,
  onChange,
}: PaymentMethodOrderModalProps) {
  // Build ordered list of enabled methods
  const initialItems = useMemo(() => {
    const items: OrderItem[] = []
    for (const [key, entry] of Object.entries(value)) {
      if (!entry.enabled) continue
      // Skip express checkout entries — they render separately
      if (key.startsWith('express_checkout:')) continue
      items.push({
        key,
        label: entry.label || key,
        logoUrl: entry.logoUrl,
        type: entry.type,
      })
    }
    // Sort by existing order, then by key as fallback
    items.sort((a, b) => {
      const orderA = value[a.key]?.order ?? Infinity
      const orderB = value[b.key]?.order ?? Infinity
      if (orderA !== orderB) return orderA - orderB
      return 0
    })
    return items
  }, [value])

  const [items, setItems] = useState<OrderItem[]>(initialItems)

  // Reset items when modal opens with new data
  const prevOpen = useMemo(() => ({ current: false }), [])
  if (open && !prevOpen.current) {
    prevOpen.current = true
    setItems(initialItems)
  } else if (!open && prevOpen.current) {
    prevOpen.current = false
  }

  const moveItem = useCallback((index: number, direction: -1 | 1) => {
    setItems((prev) => {
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= prev.length) return prev
      const next = [...prev]
      const temp = next[index]
      next[index] = next[newIndex]
      next[newIndex] = temp
      return next
    })
  }, [])

  const handleSave = useCallback(() => {
    const updated = { ...value }
    items.forEach((item, index) => {
      if (updated[item.key]) {
        updated[item.key] = { ...updated[item.key], order: index }
      }
    })
    onChange(updated)
    onOpenChange(false)
  }, [items, value, onChange, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reorder Payment Methods</DialogTitle>
          <DialogDescription>
            Set the display order of payment methods in checkout. First method is selected by default.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 py-2 max-h-[400px] overflow-y-auto">
          {items.map((item, index) => (
            <div
              key={item.key}
              className="flex items-center gap-2 rounded-md border bg-card px-3 py-2"
            >
              <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />

              {item.logoUrl ? (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border bg-white">
                  <img
                    src={item.logoUrl}
                    alt={item.label}
                    className="h-4 w-4 object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted">
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {index + 1}
                  </span>
                </div>
              )}

              <span className="flex-1 text-sm font-medium truncate">
                {item.label}
              </span>

              <span className="text-[10px] text-muted-foreground tabular-nums w-4 text-center">
                {index + 1}
              </span>

              <div className="flex flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() => moveItem(index, -1)}
                  disabled={index === 0}
                  className={cn(
                    'rounded p-0.5 hover:bg-muted transition-colors',
                    index === 0 && 'opacity-30 cursor-not-allowed',
                  )}
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(index, 1)}
                  disabled={index === items.length - 1}
                  className={cn(
                    'rounded p-0.5 hover:bg-muted transition-colors',
                    index === items.length - 1 && 'opacity-30 cursor-not-allowed',
                  )}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}

          {items.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No enabled payment methods to reorder.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={items.length === 0}>
            Save Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
